import {useTranslation} from 'react-i18next';
import styled, {css, keyframes} from 'styled-components';
import useSWRInfinite from 'swr/infinite';
import {FormEvent, useCallback, useState} from 'react';
import format from 'date-fns/format';
import {getEmailsLogs} from '../../utils/api';
import SearchIcon from '../../assets/images/icon-search.svg';
import IconReverse from '../../assets/images/icon-reverse.svg';

const PAGE_SIZE = 10;

const Logs = ({
  jwtToken,
  projectToken,
}: {
  jwtToken: string;
  projectToken: string;
}) => {
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState<'asc' | 'desc'>('asc');
  const {t} = useTranslation();

  const getKey = (pageIndex: number, previousPageData: []) => {
    if (previousPageData && !previousPageData.length) {
      return null;
    }
    return `?page=${
      pageIndex + 1
    }&token=${projectToken}&num=${PAGE_SIZE}&ordering=${ordering}&text=${search}`;
  };

  const {data, error, isValidating, size, setSize} = useSWRInfinite(
    getKey,
    (baseQuery) => getEmailsLogs(jwtToken || '', baseQuery),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      revalidateFirstPage: false,
      persistSize: true,
    }
  );

  const logs = data ? ([] as HRLog[]).concat(...data) : [];
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setText('');
      setSearch(text);
    },
    [text]
  );

  const handleSearchReset = useCallback(() => {
    setSearch('');
  }, []);

  const list = () => {
    if (error) {
      return <ErrorMessage>{t('dashboard.logs.error')}</ErrorMessage>;
    }

    if (isEmpty) {
      return <EmptyMessage>{t('dashboard.logs.empty')}</EmptyMessage>;
    }

    return logs?.map((el, i) => (
      <TableRow key={i}>
        <TableData>{format(new Date(el.sendAt), 'dd/MM/yyyy')}</TableData>
        <TableData>{el.userHr}</TableData>
        <TableData>{el.participant}</TableData>
      </TableRow>
    ));
  };

  return (
    <>
      <Intro>
        <h2>{t('dashboard.logs.title')}</h2>
        <p dangerouslySetInnerHTML={{__html: t('dashboard.logs.intro')}} />
      </Intro>

      <Body>
        <Form onSubmit={handleSubmit}>
          <SearchBar>
            <IconSearch>Search</IconSearch>
            <Input
              id="search"
              type="text"
              name="fullName"
              value={text}
              placeholder={t('dashboard.logs.search-by-text')}
              autoComplete="off"
              onChange={(e) => setText(e.target.value)}
            />
          </SearchBar>
        </Form>

        {search && (
          <Searching>
            {t('dashboard.logs.filter-added')}: <span>{search}</span>
            <button onClick={handleSearchReset}>
              <u>{t('dashboard.logs.remove')}</u>
            </button>
          </Searching>
        )}

        <TableContainer>
          <Table>
            <TableHeadRow>
              <TableHead>
                <ChangeOrderButton
                  onClick={() =>
                    setOrdering(ordering === 'asc' ? 'desc' : 'asc')
                  }
                >
                  <span>{t('dashboard.logs.date')}</span>
                  <IconReverseImg src={IconReverse} />
                </ChangeOrderButton>
              </TableHead>
              <TableHead>{t('dashboard.logs.from')}</TableHead>
              <TableHead>{t('dashboard.logs.to')}</TableHead>
            </TableHeadRow>
            <TableResults>{list()}</TableResults>
          </Table>
        </TableContainer>

        {!error && !isValidating && !isReachingEnd && logs !== undefined && (
          <LoadMore onClick={() => setSize(size + 1)}>
            {t('dashboard.logs.load-more')}
          </LoadMore>
        )}

        {isValidating && !data && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
      </Body>
    </>
  );
};

export default Logs;

const Intro = styled.div`
  h2 {
    color: ${(p) => p.theme.colors.violet500};
    font-weight: 700;
    font-size: 1.75rem;
    font-family: 'Mulish', sans-serif;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.125rem;
    line-height: 1.5;
  }
`;

const Body = styled.div`
  /* padding-bottom: 3.125rem; */
`;

const Form = styled.form`
  margin: 24px 0 16px;
  border: solid 1px #e5e5e5;
  border-radius: 10px;
  padding: 5px 24px;
`;

const SearchBar = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Input = styled.input`
  flex: 1;
  display: block;
  padding: 0 8px;
  font-size: 16px;
  font-weight: 400;
  border: none;
  font-family: inherit;
  outline: none;
  border-radius: 10px;
  line-height: 40px;

  &:hover {
    box-shadow: 0 0 2px #360cc9;
  }

  &::placeholder {
    color: #bdbbc9;
  }
`;

const IconSearch = styled.i`
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  text-indent: 100%;
  overflow: hidden;
  white-space: nowrap;
  background-image: url(${SearchIcon});
  background-position: center;
  background-size: 100%;
  background-repeat: no-repeat;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2.5rem 0;
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Loader = styled.div`
  width: 32px;
  height: 32px;
  border: solid 4px ${(p) => p.theme.colors.orange600};
  border-bottom-color: transparent;
  border-radius: 50%;
  background-color: transparent;
  animation: ${rotate} infinite linear 800ms;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const Table = styled.div`
  min-width: 520px;
  position: relative;
`;

const TableResults = styled.div`
  max-height: 50vh;
  overflow-y: scroll;
`;

const TableRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  border-radius: 4px;
  padding: 16px 20px;
  background-color: ${(p) => p.theme.colors.violet100};

  & + & {
    margin-top: 8px;
  }
`;

const TableHeadRow = styled(TableRow)`
  margin-bottom: 16px;
  background-color: ${(p) => p.theme.colors.orange100};
`;

const TableData = styled.div`
  flex-basis: calc(100% / 3);
  text-align: left;
  font-size: 13px;
  font-family: 'Bossa', sans-serif;
  word-break: break-all;
  padding: 2px 4px;

  strong {
    color: ${(p) => p.theme.colors.violet500};
  }
`;

const TableHead = styled(TableData)<{active?: boolean}>`
  display: inline-flex;
  justify-content: flex-start;
  gap: 4px;
  color: ${(p) => p.theme.colors.orange600};
  font-weight: 300;
  font-size: 13px;

  ${(p) =>
    p.active &&
    css`
      font-weight: 700;
    `}

  button {
    all: unset;
    cursor: pointer;
  }
`;

const LoadMore = styled.button`
  display: flex;
  margin: 2.5rem auto 0;
  padding: 0;
  border: none;
  text-align: center;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
  background-color: transparent;
  color: ${(p) => p.theme.colors.orange500};
  text-decoration: none;
  font-family: inherit;
`;

const IconReverseImg = styled.img`
  display: block;
  margin: 0;
  width: 16px;
  height: 16px;
`;

const ChangeOrderButton = styled.button`
  margin: 0 !important;
  padding: 0 !important;
  background-color: transparent !important;
  border: none !important;
  display: inline-flex !important;
  gap: 0.25rem !important;
`;

const Searching = styled.p`
  margin-top: 1rem;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  column-gap: 12px;

  span {
    display: block;
    padding: 3px 6px;
    background-color: ${(p) => p.theme.colors.violet300};
    color: ${(p) => p.theme.colors.white};
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
    border-radius: 16px;
  }

  button {
    all: unset;
    display: block;
    cursor: pointer;
  }
`;

const EmptyMessage = styled.h4`
  margin: 24px auto 0;
  text-align: center;
  font-weight: 400;
  font-family: 'Bossa', sans-serif;
`;

const ErrorMessage = styled(EmptyMessage)`
  color: ${(p) => p.theme.colors.red};
`;
